import React, { useCallback, useEffect, useRef, useState } from 'react';
import FJTrimVideoManager from '@src/manager/FJTrimVideoManager.ts';
import styles from './FJVideo.module.less';
import FJVideoController from '@src/components/FJVideoPreviewPlayer/FJVideoController.tsx';
import PlayBigIcon from '@src/assets/play-big.svg?react';

function formatTime(sec: number): string {
    const m = Math.floor(sec / 60);
    const s = Math.floor(sec % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
}

interface Props {
    displayWidth: number;
    displayHeight: number;
    duration: number;
    currentTime: number;
    isPlaying: boolean;
    onSeek?: (time: number) => void;
    onPlay?: () => void;
    onPause?: () => void;
    onLoadedCallback?: () => void;
}

const Video = ({
    displayWidth,
    displayHeight,
    duration,
    currentTime,
    isPlaying,
    onSeek,
    onPlay,
    onPause,
    onLoadedCallback,
}: Props) => {
    const mainVideoRef = useRef<HTMLVideoElement>(null);
    const [isBuffering, setIsBuffering] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        const mainVideo = mainVideoRef.current as HTMLVideoElement;
        FJTrimVideoManager.setVideoElement(mainVideo);
        return () => {
            FJTrimVideoManager.setVideoElement(null);
        };
    }, []);

    const startLoading = () => {
        setIsLoading(true);
    };
    const endLoading = () => {
        onLoadedCallback?.();
        setIsLoading(false);
    };

    const onWaiting = () => {
        setIsBuffering(true);
    };

    const endWaiting = () => {
        setIsBuffering(false);
    };

    const _onSeek = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            const time = Number(e.target.value);
            if (Number.isFinite(time) && time >= 0 && time <= duration) {
                onSeek?.(time);
            }
        },
        [duration, onSeek],
    );

    const progress = duration > 0 ? Math.min(currentTime / duration, 1) : 0;
    const showCenterPlayIcon = duration > 0 && !isPlaying && !isBuffering && !isLoading;

    const handleTogglePlay = useCallback(() => {
        if (isPlaying) onPause?.();
        else onPlay?.();
    }, [isPlaying, onPause, onPlay]);

    return (
        <>
            <div
                className={styles.videoContainer}
                style={{ width: displayWidth, height: displayHeight }}
                onClick={handleTogglePlay}
            >
                {(isBuffering || isLoading) && (
                    <div className={styles.loadingOverlay}>
                        <div className={styles.spinner}></div>
                    </div>
                )}
                {showCenterPlayIcon && (
                    <div className={styles.center_hud} aria-hidden>
                        <div className={styles.center_play_disc}>
                            <PlayBigIcon className={styles.center_play_svg} aria-hidden />
                        </div>
                    </div>
                )}
                <video
                    ref={mainVideoRef}
                    className={styles.video}
                    playsInline
                    onLoadStart={startLoading}
                    onLoadedMetadata={endLoading}
                    onWaiting={onWaiting}
                    onPlaying={endWaiting}
                    onSeeking={onWaiting}
                    onSeeked={endWaiting}
                />
            </div>
            <div
                className={styles.controls}
                style={{ display: !isLoading ? 'block' : 'none' }}
                onClick={e => e.stopPropagation()}
            >
                <FJVideoController
                    segmentDuration={duration}
                    clampedTime={currentTime}
                    progress={progress}
                    timeLabel={`${formatTime(currentTime)} / ${formatTime(duration)}`}
                    isPlaying={isPlaying}
                    isFullscreen={false}
                    onTogglePlayPause={() => {
                        if (isPlaying) onPause?.();
                        else onPlay?.();
                    }}
                    onSeek={_onSeek}
                    disableFullscreen={true}
                />
            </div>
        </>
    );
};

export default React.memo(Video);
